import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useLanguage } from '../i18n/LanguageContext';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import './Profile.css';

const learningLanguageFlags = {
    italian: '🇮🇹', spanish: '🇪🇸', french: '🇫🇷', english: '🇬🇧', portuguese: '🇧🇷',
};
const learningLanguageNamesByInterfaceLang = {
    en: { italian: 'Italian', spanish: 'Spanish', french: 'French', english: 'English', portuguese: 'Portuguese' },
    it: { italian: 'Italiano', spanish: 'Spagnolo', french: 'Francese', english: 'Inglese', portuguese: 'Portoghese' },
    es: { italian: 'Italiano', spanish: 'Español', french: 'Francés', english: 'Inglés', portuguese: 'Portugués' },
    fr: { italian: 'Italien', spanish: 'Espagnol', french: 'Français', english: 'Anglais', portuguese: 'Portugais' },
    pt: { italian: 'Italiano', spanish: 'Espanhol', french: 'Francês', english: 'Inglês', portuguese: 'Português' },
};

const profileText = {
    en: {
        soon: 'Soon',
        save: 'Save',
        saving: 'Saving...',
        close: 'Close',
        saveFailed: 'Something went wrong. Please try again.',
        nameTitle: 'Display name',
        nameDesc: 'This is the name Vozoly uses to greet you.',
        nameInvalid: 'Please enter a name between 2 and 40 characters.',
        emailTitle: 'Change email address',
        emailDesc: 'We will send a confirmation link to your new address.',
        newEmail: 'New email address',
        emailInvalid: 'Please enter a valid email address.',
        emailSame: 'That is already your email address.',
        emailSent: 'We sent a confirmation link to {email}. Your address changes once you confirm it.',
        passwordTitle: 'Change password',
        currentPassword: 'Current password',
        newPassword: 'New password (at least 8 characters)',
        confirmPassword: 'Confirm new password',
        passwordShort: 'Your new password must be at least 8 characters.',
        passwordMismatch: 'The two new passwords do not match.',
        wrongPassword: 'Your current password is not correct.',
        passwordUpdated: 'Your password has been updated.',
        planTitle: 'Premium is coming soon',
        planDesc: 'Premium will unlock every topic, unlimited Gabi conversations, exam prep and the full stats. Join the list and we will tell you when it launches.',
        notifyMe: 'Notify me',
        onTheList: 'You are on the list',
    },
    it: {
        soon: 'Presto',
        save: 'Salva',
        saving: 'Salvataggio...',
        close: 'Chiudi',
        saveFailed: 'Qualcosa è andato storto. Riprova.',
        nameTitle: 'Nome visualizzato',
        nameDesc: 'È il nome che Vozoly usa per salutarti.',
        nameInvalid: 'Inserisci un nome tra 2 e 40 caratteri.',
        emailTitle: 'Cambia indirizzo email',
        emailDesc: 'Invieremo un link di conferma al tuo nuovo indirizzo.',
        newEmail: 'Nuovo indirizzo email',
        emailInvalid: 'Inserisci un indirizzo email valido.',
        emailSame: 'Questo è già il tuo indirizzo email.',
        emailSent: 'Abbiamo inviato un link di conferma a {email}. L\'indirizzo cambierà quando lo confermi.',
        passwordTitle: 'Cambia password',
        currentPassword: 'Password attuale',
        newPassword: 'Nuova password (almeno 8 caratteri)',
        confirmPassword: 'Conferma la nuova password',
        passwordShort: 'La nuova password deve avere almeno 8 caratteri.',
        passwordMismatch: 'Le due nuove password non coincidono.',
        wrongPassword: 'La password attuale non è corretta.',
        passwordUpdated: 'La tua password è stata aggiornata.',
        planTitle: 'Premium arriva presto',
        planDesc: 'Premium sbloccherà tutti gli argomenti, conversazioni illimitate con Gabi, preparazione agli esami e statistiche complete. Iscriviti alla lista e ti avviseremo al lancio.',
        notifyMe: 'Avvisami',
        onTheList: 'Sei nella lista',
    },
    es: {
        soon: 'Pronto',
        save: 'Guardar',
        saving: 'Guardando...',
        close: 'Cerrar',
        saveFailed: 'Algo salió mal. Inténtalo de nuevo.',
        nameTitle: 'Nombre visible',
        nameDesc: 'Es el nombre con el que Vozoly te saluda.',
        nameInvalid: 'Escribe un nombre de entre 2 y 40 caracteres.',
        emailTitle: 'Cambiar correo electrónico',
        emailDesc: 'Enviaremos un enlace de confirmación a tu nueva dirección.',
        newEmail: 'Nuevo correo electrónico',
        emailInvalid: 'Escribe un correo electrónico válido.',
        emailSame: 'Ese ya es tu correo electrónico.',
        emailSent: 'Enviamos un enlace de confirmación a {email}. Tu dirección cambiará cuando lo confirmes.',
        passwordTitle: 'Cambiar contraseña',
        currentPassword: 'Contraseña actual',
        newPassword: 'Nueva contraseña (mínimo 8 caracteres)',
        confirmPassword: 'Confirma la nueva contraseña',
        passwordShort: 'La nueva contraseña debe tener al menos 8 caracteres.',
        passwordMismatch: 'Las dos contraseñas nuevas no coinciden.',
        wrongPassword: 'La contraseña actual no es correcta.',
        passwordUpdated: 'Tu contraseña se ha actualizado.',
        planTitle: 'Premium llega pronto',
        planDesc: 'Premium desbloqueará todos los temas, conversaciones ilimitadas con Gabi, preparación de exámenes y estadísticas completas. Únete a la lista y te avisaremos cuando llegue.',
        notifyMe: 'Avísame',
        onTheList: 'Estás en la lista',
    },
    fr: {
        soon: 'Bientôt',
        save: 'Enregistrer',
        saving: 'Enregistrement...',
        close: 'Fermer',
        saveFailed: 'Une erreur s\'est produite. Réessaie.',
        nameTitle: 'Nom affiché',
        nameDesc: 'C\'est le nom que Vozoly utilise pour te saluer.',
        nameInvalid: 'Saisis un nom de 2 à 40 caractères.',
        emailTitle: 'Changer d\'adresse e-mail',
        emailDesc: 'Nous enverrons un lien de confirmation à ta nouvelle adresse.',
        newEmail: 'Nouvelle adresse e-mail',
        emailInvalid: 'Saisis une adresse e-mail valide.',
        emailSame: 'C\'est déjà ton adresse e-mail.',
        emailSent: 'Nous avons envoyé un lien de confirmation à {email}. Ton adresse changera une fois que tu l\'auras confirmée.',
        passwordTitle: 'Changer de mot de passe',
        currentPassword: 'Mot de passe actuel',
        newPassword: 'Nouveau mot de passe (8 caractères minimum)',
        confirmPassword: 'Confirme le nouveau mot de passe',
        passwordShort: 'Le nouveau mot de passe doit contenir au moins 8 caractères.',
        passwordMismatch: 'Les deux nouveaux mots de passe ne correspondent pas.',
        wrongPassword: 'Le mot de passe actuel est incorrect.',
        passwordUpdated: 'Ton mot de passe a été mis à jour.',
        planTitle: 'Premium arrive bientôt',
        planDesc: 'Premium débloquera tous les sujets, des conversations illimitées avec Gabi, la préparation aux examens et les statistiques complètes. Inscris-toi à la liste et nous te préviendrons au lancement.',
        notifyMe: 'Me prévenir',
        onTheList: 'Tu es sur la liste',
    },
    pt: {
        soon: 'Em breve',
        save: 'Salvar',
        saving: 'Salvando...',
        close: 'Fechar',
        saveFailed: 'Algo deu errado. Tente novamente.',
        nameTitle: 'Nome de exibição',
        nameDesc: 'É o nome que o Vozoly usa para cumprimentar você.',
        nameInvalid: 'Digite um nome com 2 a 40 caracteres.',
        emailTitle: 'Alterar endereço de e-mail',
        emailDesc: 'Enviaremos um link de confirmação para o seu novo endereço.',
        newEmail: 'Novo endereço de e-mail',
        emailInvalid: 'Digite um endereço de e-mail válido.',
        emailSame: 'Esse já é o seu endereço de e-mail.',
        emailSent: 'Enviamos um link de confirmação para {email}. Seu endereço muda assim que você confirmar.',
        passwordTitle: 'Alterar senha',
        currentPassword: 'Senha atual',
        newPassword: 'Nova senha (mínimo de 8 caracteres)',
        confirmPassword: 'Confirme a nova senha',
        passwordShort: 'A nova senha deve ter pelo menos 8 caracteres.',
        passwordMismatch: 'As duas novas senhas não coincidem.',
        wrongPassword: 'A senha atual não está correta.',
        passwordUpdated: 'Sua senha foi atualizada.',
        planTitle: 'O Premium chega em breve',
        planDesc: 'O Premium vai liberar todos os temas, conversas ilimitadas com a Gabi, preparação para exames e estatísticas completas. Entre na lista e avisaremos quando for lançado.',
        notifyMe: 'Avise-me',
        onTheList: 'Você está na lista',
    },
};

const appLanguageOptions = [
    { code: 'en', flag: '🇬🇧', name: 'English' },
    { code: 'it', flag: '🇮🇹', name: 'Italiano' },
    { code: 'es', flag: '🇪🇸', name: 'Español' },
    { code: 'fr', flag: '🇫🇷', name: 'Français' },
    { code: 'pt', flag: '🇧🇷', name: 'Português' },
];

function Profile() {
    const navigate = useNavigate();
    const { t, interfaceLanguage, setInterfaceLanguage } = useLanguage();
    const { activeLanguage, activeLevel, userLanguages, switchLanguage, deleteLanguage } = useUserLanguage();
    const [removeLangTarget, setRemoveLangTarget] = useState(null);
    const [removeLangError, setRemoveLangError] = useState('');
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAppLangPicker, setShowAppLangPicker] = useState(false);
    const [activeTab, setActiveTab] = useState('progress');
    const [profile, setProfile] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [editModal, setEditModal] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [editCurrentPassword, setEditCurrentPassword] = useState('');
    const [editNewPassword, setEditNewPassword] = useState('');
    const [editConfirmPassword, setEditConfirmPassword] = useState('');
    const [editError, setEditError] = useState('');
    const [editNotice, setEditNotice] = useState('');
    const [editSaving, setEditSaving] = useState(false);
    const [showPlanModal, setShowPlanModal] = useState(false);
    const [waitlistSaving, setWaitlistSaving] = useState(false);
    const s = (key) => (profileText[interfaceLanguage] || profileText.en)[key] || profileText.en[key];

    useEffect(() => {
        const getProfile = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) { navigate('/signin'); return; }
            setUser(user);
            const { data } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', user.id)
                .single();
            if (data) setProfile(data);
            setLoading(false);
        };
        getProfile();
    }, [navigate]);

    useEffect(() => {
        const loadStats = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            const { data: statsRow } = await supabase
                .from('user_stats')
                .select('total_xp')
                .eq('user_id', user.id)
                .maybeSingle();
            if (statsRow) setTotalXp(statsRow.total_xp || 0);

            const statsLang = (activeLanguage || localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();
            const { count: lessonsCount } = await supabase
                .from('lesson_results')
                .select('id', { count: 'exact', head: true })
                .eq('user_id', user.id)
                .eq('target_language', statsLang);
            setLessonsCompletedCount(lessonsCount || 0);

            const { count: wordsCount } = await supabase
                .from('word_bank')
                .select('id', { count: 'exact', head: true })
                .eq('user_id', user.id)
                .eq('target_language', statsLang);
            setWordsSavedCount(wordsCount || 0);

            setDayStreak(0);
            setHoursStudied(0);
        };
        loadStats();
    }, [activeLanguage]);

    const handleDeleteAccount = async () => {
        setDeleteError('');
        setDeleteLoading(true);

        const { error: signInError } = await supabase.auth.signInWithPassword({
            email: user.email,
            password: deletePassword,
        });

        if (signInError) {
            setDeleteError(s('wrongPassword'));
            setDeleteLoading(false);
            return;
        }

        const { error: deleteError } = await supabase
            .from('profiles')
            .delete()
            .eq('id', user.id);

        if (deleteError) {
            setDeleteError(s('saveFailed'));
            setDeleteLoading(false);
            return;
        }

        await supabase.auth.signOut();
        navigate('/');
    };

    const closeEdit = () => {
        setEditModal(null);
        setEditValue('');
        setEditCurrentPassword('');
        setEditNewPassword('');
        setEditConfirmPassword('');
        setEditError('');
        setEditNotice('');
        setEditSaving(false);
    };

    const openEdit = (type) => {
        closeEdit();
        setEditModal(type);
        if (type === 'name') setEditValue(profile?.display_name || user?.email?.split('@')[0] || '');
    };

    const saveEdit = async () => {
        setEditError('');
        setEditNotice('');

        if (editModal === 'name') {
            const name = editValue.trim();
            if (name.length < 2 || name.length > 40) { setEditError(s('nameInvalid')); return; }
            setEditSaving(true);
            const { data, error } = await supabase
                .from('profiles')
                .update({ display_name: name })
                .eq('id', user.id)
                .select('id');
            setEditSaving(false);
            if (error || !data || data.length === 0) { setEditError(s('saveFailed')); return; }
            setProfile(prev => ({ ...prev, display_name: name }));
            try { localStorage.setItem('vozoly_display_name', name); } catch (e) { }
            closeEdit();
            return;
        }

        if (editModal === 'email') {
            const newEmail = editValue.trim().toLowerCase();
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) { setEditError(s('emailInvalid')); return; }
            if (newEmail === (user?.email || '').toLowerCase()) { setEditError(s('emailSame')); return; }
            setEditSaving(true);
            const { error } = await supabase.auth.updateUser({ email: newEmail });
            setEditSaving(false);
            if (error) { setEditError(s('saveFailed')); return; }
            setEditNotice(s('emailSent').replace('{email}', newEmail));
            return;
        }

        if (editModal === 'password') {
            if (editNewPassword.length < 8) { setEditError(s('passwordShort')); return; }
            if (editNewPassword !== editConfirmPassword) { setEditError(s('passwordMismatch')); return; }
            setEditSaving(true);
            const { error: signInError } = await supabase.auth.signInWithPassword({
                email: user.email,
                password: editCurrentPassword,
            });
            if (signInError) { setEditSaving(false); setEditError(s('wrongPassword')); return; }
            const { error } = await supabase.auth.updateUser({ password: editNewPassword });
            setEditSaving(false);
            if (error) { setEditError(s('saveFailed')); return; }
            setEditCurrentPassword('');
            setEditNewPassword('');
            setEditConfirmPassword('');
            setEditNotice(s('passwordUpdated'));
        }
    };

    const joinWaitlist = async () => {
        if (!user) return;
        setEditError('');
        setWaitlistSaving(true);
        const { data, error } = await supabase
            .from('profiles')
            .update({ premium_waitlist: true })
            .eq('id', user.id)
            .select('id');
        setWaitlistSaving(false);
        if (error || !data || data.length === 0) { setEditError(s('saveFailed')); return; }
        setProfile(prev => ({ ...prev, premium_waitlist: true }));
    };

    const tabs = [
        { id: 'progress', label: t('profile.progressTab') },
        { id: 'languages', label: t('profile.languagesTab') },
        { id: 'settings', label: t('profile.settingsTab') },
        { id: 'subscription', label: t('profile.subscriptionTab') },
    ];

    const targetLang = (activeLanguage || profile?.target_language || 'italian').toLowerCase();
    const [totalXp, setTotalXp] = useState(0);
    const [dayStreak, setDayStreak] = useState(0);
    const [lessonsCompletedCount, setLessonsCompletedCount] = useState(0);
    const [wordsSavedCount, setWordsSavedCount] = useState(0);
    const [hoursStudied, setHoursStudied] = useState(0);
    const level = activeLevel || profile?.level || 'A1';
    const displayName = profile?.display_name || user?.email?.split('@')[0] || 'there';

    if (loading) {
        return (
            <div className="prof-loading">
                <div className="prof-loading-spinner"></div>
            </div>
        );
    }

    return (
        <div className="prof-screen">

            <div className="prof-top-bar">
                <div className="prof-top-row">
                    <button className="prof-back" onClick={() => navigate('/home')}>←</button>
                    <div className="prof-title">{t('profile.myProfile')}</div>
                    <div style={{ width: 32 }}></div>
                </div>
                <div className="prof-avatar-row">
                    <div className="prof-avatar">
                        {displayName[0].toUpperCase()}
                    </div>
                    <div>
                        <div className="prof-name">{displayName}</div>
                        <div className="prof-email">{user?.email}</div>
                        <div className="prof-plan">{t('profile.freePlan')}</div>
                    </div>
                </div>
            </div>

            <div className="prof-tabs">
                {tabs.map(tab => (
                    <div
                        key={tab.id}
                        className={`prof-tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                    </div>
                ))}
            </div>

            <div className="prof-content">

                {activeTab === 'progress' && (
                    <div>
                        <div className="prof-section-label">{t('profile.currentLevel')}</div>
                        <div className="prof-row">
                            <div className="prof-row-icon">📊</div>
                            <div className="prof-row-info">
                                <div className="prof-row-title">
                                    {learningLanguageFlags[targetLang]} {(learningLanguageNamesByInterfaceLang[interfaceLanguage] || learningLanguageNamesByInterfaceLang.en)[targetLang]} · {level}
                                </div>
                                <div className="prof-row-sub">{hoursStudied} {t('profile.hrsStudied')} · {lessonsCompletedCount} {t('profile.lessonsCompleted')}</div>
                            </div>
                            <div className="prof-row-arrow">›</div>
                        </div>

                        <div className="prof-section-label">{t('profile.stats')}</div>
                        <div className="prof-stats-grid">
                            <div className="prof-stat-card">
                                <div className="prof-stat-value">{dayStreak}</div>
                                <div className="prof-stat-label">{t('profile.dayStreak')}</div>
                            </div>
                            <div className="prof-stat-card">
                                <div className="prof-stat-value">{totalXp}</div>
                                <div className="prof-stat-label">{t('profile.totalXp')}</div>
                            </div>
                            <div className="prof-stat-card">
                                <div className="prof-stat-value">{lessonsCompletedCount}</div>
                                <div className="prof-stat-label">{t('profile.lessonsDone')}</div>
                            </div>
                            <div className="prof-stat-card">
                                <div className="prof-stat-value">{wordsSavedCount}</div>
                                <div className="prof-stat-label">{t('profile.wordsSaved')}</div>
                            </div>
                        </div>

                        <div className="prof-section-label">{t('profile.levelManagement')}</div>
                        <div className="prof-row" onClick={() => { localStorage.setItem('vozoly_placement_path', 'test'); navigate('/onboarding/placement-test', { state: { retake: true } }); }}>
                            <div className="prof-row-icon">🎯</div>
                            <div className="prof-row-info">
                                <div className="prof-row-title">{t('profile.retakeTest')}</div>
                                <div className="prof-row-sub">{t('profile.retakeTestDesc')}</div>
                            </div>
                            <div className="prof-row-arrow">›</div>
                        </div>
                        <div className="prof-row" onClick={() => navigate('/onboarding/choose-level', { state: { editMode: true } })}>
                            <div className="prof-row-icon">✏️</div>
                            <div className="prof-row-info">
                                <div className="prof-row-title">{t('profile.changeLevelManually')}</div>
                                <div className="prof-row-sub">{t('profile.currently')} {level}</div>
                            </div>
                            <div className="prof-row-arrow">›</div>
                        </div>
                    </div>
                )}

                {activeTab === 'languages' && (
                    <div>
                        <div className="prof-section-label">{t('profile.learning')}</div>
                        {userLanguages.map(lang => (
                            <div
                                key={lang.target_language}
                                className="prof-lang-card"
                                onClick={() => lang.target_language !== activeLanguage && switchLanguage(lang.target_language)}
                                style={{ cursor: lang.target_language !== activeLanguage ? 'pointer' : 'default' }}
                            >
                                <div className="prof-lang-flag">{learningLanguageFlags[lang.target_language]}</div>
                                <div className="prof-lang-info">
                                    <div className="prof-lang-name">{(learningLanguageNamesByInterfaceLang[interfaceLanguage] || learningLanguageNamesByInterfaceLang.en)[lang.target_language]}</div>
                                    <div className="prof-lang-level">{lang.level} · {lang.target_language === activeLanguage ? t('profile.active') : t('profile.tapToSwitch')}</div>
                                </div>
                                {lang.target_language === activeLanguage ? (
                                    <div className="prof-lang-active-badge">{t('profile.active')}</div>
                                ) : (
                                    <button
                                        className="prof-lang-delete-btn"
                                        onClick={(e) => { e.stopPropagation(); setRemoveLangTarget(lang.target_language); }}
                                    >
                                        🗑️
                                    </button>
                                )}
                            </div>
                        ))}

                        <div className="prof-add-lang" onClick={() => navigate('/add-language')}>
                            <div className="prof-add-lang-icon">➕</div>
                            <div className="prof-add-lang-text">
                                <div className="prof-add-lang-title">{t('profile.addAnotherLanguage')}</div>
                                <div className="prof-add-lang-sub">{t('profile.learnMultiple')}</div>
                            </div>
                        </div>

                        <div className="prof-section-label">{t('profile.appLanguage')}</div>
                        <div className="prof-row" onClick={() => setShowAppLangPicker(true)}>
                            <div className="prof-row-icon">🌐</div>
                            <div className="prof-row-info">
                                <div className="prof-row-title">{t('profile.appLanguage')}</div>
                                <div className="prof-row-sub">
                                    {appLanguageOptions.find(l => l.code === interfaceLanguage)?.flag} {appLanguageOptions.find(l => l.code === interfaceLanguage)?.name}
                                </div>
                            </div>
                            <div className="prof-row-arrow">›</div>
                        </div>
                    </div>
                )}

                {removeLangTarget && (
                    <div className="prof-modal-overlay" onClick={() => { setRemoveLangTarget(null); setRemoveLangError(''); }}>
                        <div className="prof-modal" onClick={e => e.stopPropagation()}>
                            <div className="prof-modal-title">{t('profile.removeLanguageTitle')}</div>
                            <div className="prof-modal-desc">
                                {t('profile.removeLanguageDesc')}
                            </div>
                            {removeLangError && (
                                <div className="prof-modal-error">⚠️ {removeLangError}</div>
                            )}
                            <div className="prof-modal-actions">
                                <button className="prof-modal-cancel" onClick={() => { setRemoveLangTarget(null); setRemoveLangError(''); }}>
                                    {t('profile.cancel')}
                                </button>
                                <button
                                    className="prof-modal-confirm-danger"
                                    onClick={async () => {
                                        const result = await deleteLanguage(removeLangTarget);
                                        if (result.success) {
                                            setRemoveLangTarget(null);
                                            setRemoveLangError('');
                                        } else if (result.error === 'Cannot delete the active language') {
                                            setRemoveLangError(t('profile.mustSwitchFirst'));
                                        } else {
                                            setRemoveLangError(result.error);
                                        }
                                    }}
                                >
                                    {t('profile.remove')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'settings' && (
                    <div>
                        <div className="prof-section-label">{t('profile.account')}</div>
                        <div className="prof-row" onClick={() => openEdit('name')}>
                            <div className="prof-row-icon">👤</div>
                            <div className="prof-row-info">
                                <div className="prof-row-title">{t('profile.displayName')}</div>
                                <div className="prof-row-sub">{displayName}</div>
                            </div>
                            <div className="prof-row-arrow">›</div>
                        </div>
                        <div className="prof-row" style={{ cursor: 'default' }}>
                            <div className="prof-row-icon">📧</div>
                            <div className="prof-row-info">
                                <div className="prof-row-title">{t('profile.emailAddress')}</div>
                                <div className="prof-row-sub">{user?.email}</div>
                            </div>
                        </div>
                        <div className="prof-row" onClick={() => openEdit('password')}>
                            <div className="prof-row-icon">🔒</div>
                            <div className="prof-row-info">
                                <div className="prof-row-title">{t('profile.changePassword')}</div>
                                <div className="prof-row-sub">{t('profile.updatePassword')}</div>
                            </div>
                            <div className="prof-row-arrow">›</div>
                        </div>

                        <div className="prof-section-label">{t('profile.prefs')}</div>
                        <div className="prof-row">
                            <div className="prof-row-icon">🔔</div>
                            <div className="prof-row-info">
                                <div className="prof-row-title">{t('profile.notifications')}</div>
                                <div className="prof-row-sub">{t('profile.notificationsDesc')}</div>
                            </div>
                            <div className="prof-row-arrow" style={{ fontSize: 12 }}>{s('soon')}</div>
                        </div>
                        <div className="prof-row">
                            <div className="prof-row-icon">🎵</div>
                            <div className="prof-row-info">
                                <div className="prof-row-title">{t('profile.soundEffects')}</div>
                                <div className="prof-row-sub">{t('profile.on')}</div>
                            </div>
                            <div className="prof-row-arrow" style={{ fontSize: 12 }}>{s('soon')}</div>
                        </div>

                        <div className="prof-section-label">{t('profile.support')}</div>
                        <div className="prof-row">
                            <div className="prof-row-icon">❓</div>
                            <div className="prof-row-info">
                                <div className="prof-row-title">{t('profile.helpSupport')}</div>
                                <div className="prof-row-sub">{t('profile.helpSupportDesc')}</div>
                            </div>
                            <div className="prof-row-arrow" style={{ fontSize: 12 }}>{s('soon')}</div>
                        </div>
                        <div className="prof-row">
                            <div className="prof-row-icon">📋</div>
                            <div className="prof-row-info">
                                <div className="prof-row-title">{t('profile.termsPrivacy')}</div>
                                <div className="prof-row-sub">{t('profile.termsPrivacyDesc')}</div>
                            </div>
                            <div className="prof-row-arrow" style={{ fontSize: 12 }}>{s('soon')}</div>
                        </div>

                        <div className="prof-section-label">{t('profile.dangerZone')}</div>
                        <div className="prof-row danger" onClick={() => setShowDeleteModal(true)}>
                            <div className="prof-row-icon">🗑️</div>
                            <div className="prof-row-info">
                                <div className="prof-row-title danger">{t('profile.deleteMyAccount')}</div>
                                <div className="prof-row-sub">{t('profile.deleteMyAccountDesc')}</div>
                            </div>
                            <div className="prof-row-arrow" style={{ color: '#A32D2D' }}>›</div>
                        </div>

                        {showDeleteModal && (
                            <div className="prof-modal-overlay" onClick={() => setShowDeleteModal(false)}>
                                <div className="prof-modal" onClick={e => e.stopPropagation()}>
                                    <div className="prof-modal-title">{t('profile.deleteAccountTitle')}</div>
                                    <div className="prof-modal-desc">
                                        {t('profile.deleteAccountWarning')}
                                    </div>
                                    <div className="prof-modal-desc" style={{ color: '#A32D2D', fontWeight: 500, marginTop: 8 }}>
                                        {t('profile.enterPasswordConfirm')}
                                    </div>
                                    <input
                                        className="prof-modal-input"
                                        type="password"
                                        placeholder={t('profile.yourPassword')}
                                        value={deletePassword}
                                        onChange={e => setDeletePassword(e.target.value)}
                                        autoFocus
                                    />
                                    {deleteError && (
                                        <div className="prof-modal-error">⚠️ {deleteError}</div>
                                    )}
                                    <div className="prof-modal-actions">
                                        <button className="prof-modal-cancel" onClick={() => {
                                            setShowDeleteModal(false);
                                            setDeletePassword('');
                                            setDeleteError('');
                                        }}>
                                            {t('profile.cancel')}
                                        </button>
                                        <button
                                            className="prof-modal-confirm-danger"
                                            disabled={!deletePassword || deleteLoading}
                                            onClick={handleDeleteAccount}
                                        >
                                            {deleteLoading ? t('profile.deleting') : t('profile.deleteAccountBtn')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'subscription' && (
                    <div>
                        <div className="prof-current-plan">
                            <div className="prof-plan-icon">🌱</div>
                            <div className="prof-plan-info">
                                <div className="prof-plan-name">{t('profile.freePlan')}</div>
                                <div className="prof-plan-desc">
                                    {t('profile.currentPlanFree')}
                                </div>
                            </div>
                        </div>

                        <div className="prof-upgrade-card">
                            <div className="prof-upgrade-title">{t('profile.unlockEverything')}</div>
                            <div className="prof-upgrade-desc">
                                {t('profile.unlockDesc')}
                            </div>
                            <div className="prof-upgrade-features">
                                <div className="prof-upgrade-feature">✓ {t('profile.featureAllTopics')}</div>
                                <div className="prof-upgrade-feature">✓ {t('profile.featureUnlimitedLuna')}</div>
                                <div className="prof-upgrade-feature">✓ {t('profile.featureExamPrep')}</div>
                                <div className="prof-upgrade-feature">✓ {t('profile.featureUnlimitedWordBank')}</div>
                                <div className="prof-upgrade-feature">✓ {t('profile.featureFullStats')}</div>
                            </div>
                            <button className="prof-upgrade-btn" onClick={() => { setEditError(''); setShowPlanModal(true); }}>
                                {t('profile.seePlans')} →
                            </button>
                        </div>
                    </div>
                )}

            </div>

            {editModal && (
                <div className="prof-modal-overlay" onClick={closeEdit}>
                    <div className="prof-modal" onClick={e => e.stopPropagation()}>
                        <div className="prof-modal-title">
                            {editModal === 'name' ? s('nameTitle') : editModal === 'email' ? s('emailTitle') : s('passwordTitle')}
                        </div>

                        {editNotice ? (
                            <>
                                <div className="prof-modal-desc">{editNotice}</div>
                                <div className="prof-modal-actions">
                                    <button className="prof-modal-cancel" onClick={closeEdit}>{s('close')}</button>
                                </div>
                            </>
                        ) : (
                            <>
                                {editModal === 'name' && (
                                    <>
                                        <div className="prof-modal-desc">{s('nameDesc')}</div>
                                        <input
                                            className="prof-modal-input"
                                            type="text"
                                            maxLength={40}
                                            value={editValue}
                                            onChange={e => setEditValue(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && !editSaving && saveEdit()}
                                            autoFocus
                                        />
                                    </>
                                )}
                                {editModal === 'email' && (
                                    <>
                                        <div className="prof-modal-desc">{s('emailDesc')}</div>
                                        <input
                                            className="prof-modal-input"
                                            type="email"
                                            placeholder={s('newEmail')}
                                            value={editValue}
                                            onChange={e => setEditValue(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && !editSaving && saveEdit()}
                                            autoFocus
                                        />
                                    </>
                                )}
                                {editModal === 'password' && (
                                    <>
                                        <input
                                            className="prof-modal-input"
                                            type="password"
                                            placeholder={s('currentPassword')}
                                            value={editCurrentPassword}
                                            onChange={e => setEditCurrentPassword(e.target.value)}
                                            autoFocus
                                        />
                                        <input
                                            className="prof-modal-input"
                                            type="password"
                                            placeholder={s('newPassword')}
                                            value={editNewPassword}
                                            onChange={e => setEditNewPassword(e.target.value)}
                                            style={{ marginTop: 8 }}
                                        />
                                        <input
                                            className="prof-modal-input"
                                            type="password"
                                            placeholder={s('confirmPassword')}
                                            value={editConfirmPassword}
                                            onChange={e => setEditConfirmPassword(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && !editSaving && saveEdit()}
                                            style={{ marginTop: 8 }}
                                        />
                                    </>
                                )}
                                {editError && (
                                    <div className="prof-modal-error">⚠️ {editError}</div>
                                )}
                                <div className="prof-modal-actions">
                                    <button className="prof-modal-cancel" onClick={closeEdit}>
                                        {t('profile.cancel')}
                                    </button>
                                    <button
                                        className="prof-modal-confirm-danger"
                                        style={{ background: '#1D9E75', color: '#FFFFFF', borderColor: '#1D9E75' }}
                                        disabled={editSaving}
                                        onClick={saveEdit}
                                    >
                                        {editSaving ? s('saving') : s('save')}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {showPlanModal && (
                <div className="prof-modal-overlay" onClick={() => setShowPlanModal(false)}>
                    <div className="prof-modal" onClick={e => e.stopPropagation()}>
                        <div className="prof-modal-title">{s('planTitle')}</div>
                        <div className="prof-modal-desc">{s('planDesc')}</div>
                        {editError && (
                            <div className="prof-modal-error">⚠️ {editError}</div>
                        )}
                        <div className="prof-modal-actions">
                            <button className="prof-modal-cancel" onClick={() => setShowPlanModal(false)}>
                                {s('close')}
                            </button>
                            {profile?.premium_waitlist ? (
                                <button
                                    className="prof-modal-confirm-danger"
                                    style={{ background: '#E1F5EE', color: '#085041', borderColor: '#9FE1CB' }}
                                    disabled
                                >
                                    ✓ {s('onTheList')}
                                </button>
                            ) : (
                                <button
                                    className="prof-modal-confirm-danger"
                                    style={{ background: '#1D9E75', color: '#FFFFFF', borderColor: '#1D9E75' }}
                                    disabled={waitlistSaving}
                                    onClick={joinWaitlist}
                                >
                                    {waitlistSaving ? s('saving') : s('notifyMe')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showAppLangPicker && (
                <div className="prof-modal-overlay" onClick={() => setShowAppLangPicker(false)}>
                    <div className="prof-modal" onClick={e => e.stopPropagation()}>
                        <div className="prof-modal-title">{t('profile.appLanguage')}</div>
                        <div className="prof-modal-desc">{t('profile.chooseAppLanguage')}</div>
                        {appLanguageOptions.map(lang => (
                            <div
                                key={lang.code}
                                className={`prof-modal-option ${interfaceLanguage === lang.code ? 'selected' : ''}`}
                                onClick={() => {
                                    setInterfaceLanguage(lang.code);
                                    setShowAppLangPicker(false);
                                }}
                            >
                                {lang.flag} {lang.name}
                                {interfaceLanguage === lang.code && <span className="prof-modal-check">✓</span>}
                            </div>
                        ))}
                    </div>
                </div>
            )}

        </div>
    );
}

export default Profile;